import React, { useEffect, useMemo, useState } from "react";
import "./ProfilePage.scss";
import { Form, Flex } from "antd";
import { UserOutlined, BankOutlined, EnvironmentOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import InputField from "../../Reuseable/InputField/InputField";
import CustomSelect from "../../Reuseable/Select/Select";
import NormalButton from "../../Reuseable/NormalButton/NormalButton";
import PanelCard from "../DashboardPage/components/PanelCard";
import DatePickerWithTime from "../../Reuseable/DatePickerWithTime/DatePickerWithTime";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { fetchAgencyProfile, fetchSuperuserProfile } from "../../store/agencySlice";
import { updateProfile } from "../../assets/api/agency";
import CustomNotification from "../../Reuseable/CustomNotification/CustomNotification";
import { getUserType } from "../../assets/helpers";

const initialsOf = (name) =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();

/* "team_lead" is a database value; the badge shows it as words. */
const humanizeRole = (role) =>
  String(role || "")
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

const EMPTY = "—";

const formatStamp = (value) => {
  if (!value) return EMPTY;
  const when = dayjs(value);
  return when.isValid() ? when.format("DD MMM YYYY, hh:mm A") : EMPTY;
};

/* The superuser payload is read-only, so it is rendered as facts rather than
   as a dozen disabled inputs pretending to be a form. */
const ProfileFact = ({ label, value, loading }) => (
  <div className="profileFact">
    <span className="profileFact__label">{label}</span>
    <span className="profileFact__value">
      {loading ? <span className="profileFact__ghost" /> : value || EMPTY}
    </span>
  </div>
);

const ProfilePage = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { agency_profile, superuser_profile, superuser_profile_loading } = useSelector(
    (state) => state.agency
  );
  const [loading, setLoading] = useState(false);
  const user = useSelector((state) => state.auth.user);
  const token = useSelector((state) => state.auth.token);
  const userType = getUserType(user);
  dayjs.extend(utc);
  dayjs.extend(timezone);



  const isSuperuser = userType === "superuser";

  useEffect(() => {
    /* a superuser has no agency record behind it, so the agency endpoint
       would 404 - it reads its own account instead */
    if (isSuperuser) {
      dispatch(fetchSuperuserProfile());
    } else {
      dispatch(fetchAgencyProfile());
    }
  }, [dispatch, isSuperuser]);

  // Map profile data to form fields
  useEffect(() => {
    if (isSuperuser) return;
    if (agency_profile && Object.keys(agency_profile).length > 0) {
      if (userType === "telecaller" || userType === "supervisor") {
        form.setFieldsValue({
          name: agency_profile.name || "",
          email: agency_profile.email || "",
          mobile_no: agency_profile.mobile_no || "",
          date_of_joining: agency_profile.date_of_joining ? dayjs(agency_profile.date_of_joining).format("DD MMM YYYY") : "",
          address: agency_profile.address || "",
          city: agency_profile.city || "",
          pincode: agency_profile.pincode || "",
          mother_tongue: agency_profile.mother_tongue || "",
          agency_name: agency_profile.agency_name || "",
          field_agents: (agency_profile.field_agents || []).map(f => typeof f === "object" ? f.id : f),
          area: (agency_profile.area || []).map(a => typeof a === "object" ? a.id : a),
          tele_callers: (agency_profile.tele_callers || []).map(t => typeof t === "object" ? t.id : t),
          team_leads: (agency_profile.team_leads || []).map(t => typeof t === "object" ? t.id : t),
        });
      } else {
        form.setFieldsValue({
          companyName: agency_profile.company_name || "",
          dateTime: agency_profile.Date ? dayjs(agency_profile.Date) : null,
          cities: agency_profile.cities ? agency_profile.cities.split(",") : [],
          city: agency_profile.city || "",
          pincode: agency_profile.pin_code || "",
          registeredAddress: agency_profile.registered_address || "",
          registeredCity: agency_profile.registered_city || "",
          agencyStructure: agency_profile.agency_structure || "",
          lineOfBusiness: agency_profile.line_of_business || "",
          panNo: agency_profile.pan_no || "",
          serviceTaxNumber: agency_profile.service_tax_number || "",
          telephone: agency_profile.telephone_number || "",
          functionalArea: agency_profile.functional_area || "",
          presence_in_states: agency_profile.presence_in_states
            ? agency_profile.presence_in_states.split(",")
            : [],
        });
      }
    }
  }, [agency_profile, form, userType, isSuperuser]);

  const isStaff = userType === "telecaller" || userType === "supervisor";

  /* The page showed a form and nothing else - no name, no role, no way to
     tell whose profile you were looking at. */
  const identity = useMemo(() => {
    if (isSuperuser) {
      const fullName = [superuser_profile?.first_name, superuser_profile?.last_name]
        .filter(Boolean)
        .join(" ");
      return {
        /* first_name / last_name come back empty for most superusers, so the
           username carries the identity when they do */
        name: fullName || superuser_profile?.username || superuser_profile?.email || "Your profile",
        role: userType,
        email: superuser_profile?.email || user?.email,
        phone: null,
      };
    }
    const name = isStaff
      ? agency_profile?.name
      : agency_profile?.company_name;
    return {
      name: name || user?.name || user?.email || "Your profile",
      role: user?.selectedRole || userType,
      email: agency_profile?.email || user?.email,
      phone: isStaff
        ? agency_profile?.mobile_no
        : agency_profile?.telephone_number,
    };
  }, [agency_profile, superuser_profile, user, userType, isStaff, isSuperuser]);

  const handleSubmit = async (values) => {
    const payload = {
      // company_name: values.companyName,
      // date: values.dateTime,
      presence_in_states: Array.isArray(values.presence_in_states)
        ? values.presence_in_states.join(",")
        : "",
      cities: Array.isArray(values.cities) ? values.cities.join(",") : "",
      city: values.city,
      pin_code: values.pincode,
      registered_address: values.registeredAddress,
      registered_city: values.registeredCity,
      agency_structure: values.agencyStructure,
      line_of_business: values.lineOfBusiness,
      pan_no: values.panNo,
      service_tax_number: values.serviceTaxNumber,
      telephone_number: values.telephone,
      functional_area: values.functionalArea,
    };

    try {
      setLoading(true);
      await updateProfile(payload, token, agency_profile?.id);
      CustomNotification.show("Agency details updated successfully!");
      dispatch(fetchAgencyProfile());
      setLoading(false);
    } catch (error) {

      CustomNotification.show("Failed to update agency details.");
      setLoading(false);
    }
  };

  return (
    <div className="profilePageMain">
      <div className="profilePageContent">
        <section className="profileHero">
          <span className="profileHero__avatar" aria-hidden="true">
            {initialsOf(identity.name) || <UserOutlined />}
          </span>
          <div className="profileHero__body">
            <h2 className="profileHero__name">{identity.name}</h2>
            <div className="profileHero__meta">
              {identity.role && (
                <span className="profileHero__badge">{humanizeRole(identity.role)}</span>
              )}
              {identity.email && (
                <span className="profileHero__fact">{identity.email}</span>
              )}
              {identity.phone && (
                <span className="profileHero__fact">{identity.phone}</span>
              )}
            </div>
          </div>
        </section>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <div className="ProfileContentContainer">
            {isSuperuser ? (
              <PanelCard
                title="Account Information"
                subtitle="Your superuser account, as held by the platform."
                icon={<SafetyCertificateOutlined />}
                accent="brand"
                className="profileSection"
              >
                <div className="profileFacts">
                  <ProfileFact
                    label="Email"
                    value={superuser_profile?.email}
                    loading={superuser_profile_loading}
                  />
                  <ProfileFact
                    label="Username"
                    value={superuser_profile?.username}
                    loading={superuser_profile_loading}
                  />
                  <ProfileFact
                    label="First Name"
                    value={superuser_profile?.first_name}
                    loading={superuser_profile_loading}
                  />
                  <ProfileFact
                    label="Last Name"
                    value={superuser_profile?.last_name}
                    loading={superuser_profile_loading}
                  />
                  <ProfileFact
                    label="Date Joined"
                    value={formatStamp(superuser_profile?.date_joined)}
                    loading={superuser_profile_loading}
                  />
                  <ProfileFact
                    label="Last Login"
                    value={formatStamp(superuser_profile?.last_login)}
                    loading={superuser_profile_loading}
                  />
                </div>

                {/* the three booleans read as state, not as text fields */}
                <div className="profileFlags">
                  <span
                    className={`profileFlag ${superuser_profile?.is_active ? "is-on" : "is-off"}`}
                  >
                    <i />
                    {superuser_profile?.is_active ? "Active" : "Inactive"}
                  </span>
                  {superuser_profile?.is_superuser && (
                    <span className="profileFlag is-on">
                      <i />
                      Superuser
                    </span>
                  )}
                  {superuser_profile?.is_staff && (
                    <span className="profileFlag is-on">
                      <i />
                      Staff
                    </span>
                  )}
                </div>
              </PanelCard>
            ) : isStaff ? (
              <PanelCard
                title="Personal Information"
                subtitle="Held by your agency - contact them to change any of it."
                icon={<UserOutlined />}
                accent="brand"
                className="profileSection"
              >
                <div className="inputSections">
                  <Form.Item name="name" className="formItem wrapperSect">
                    <InputField label="Name" disabled={true} />
                  </Form.Item>
                  <Form.Item name="email" className="formItem wrapperSect">
                    <InputField label="Email" disabled={true} />
                  </Form.Item>
                  <Form.Item name="mobile_no" className="formItem wrapperSect">
                    <InputField label="Mobile Number" disabled={true} />
                  </Form.Item>
                  <Form.Item name="date_of_joining" className="formItem wrapperSect">
                    <InputField 
                      label="Date of Joining" 
                      disabled={true} 
                    />
                  </Form.Item>
                  <Form.Item name="address" className="formItem wrapperSect">
                    <InputField label="Address" disabled={true} />
                  </Form.Item>
                  <Form.Item name="city" className="formItem wrapperSect">
                    <InputField label="City" disabled={true} />
                  </Form.Item>
                  <Form.Item name="pincode" className="formItem wrapperSect">
                    <InputField label="Pincode" disabled={true} />
                  </Form.Item>
                  <Form.Item name="mother_tongue" className="formItem wrapperSect">
                    <InputField label="Mother Tongue" disabled={true} />
                  </Form.Item>
                  {userType === "supervisor" && (
                    <>
                      <Form.Item name="agency_name" className="formItem wrapperSect">
                        <InputField label="Agency Name" disabled={true} />
                      </Form.Item>
                      <Form.Item name="tele_callers" className="formItem wrapperSect">
                        <CustomSelect 
                          label="Telecallers" 
                          disabled={true} 
                          isMultiSelect={true}
                          options={(agency_profile?.tele_callers || []).map(t => ({
                            label: typeof t === "object" ? t.name : `Telecaller ${t}`,
                            value: typeof t === "object" ? t.id : t
                          }))}
                        />
                      </Form.Item>
                      <Form.Item name="team_leads" className="formItem wrapperSect">
                        <CustomSelect 
                          label="Team Leads" 
                          disabled={true} 
                          isMultiSelect={true}
                          options={(agency_profile?.team_leads || []).map(t => ({
                            label: typeof t === "object" ? t.name : `Lead ${t}`,
                            value: typeof t === "object" ? t.id : t
                          }))}
                        />
                      </Form.Item>
                    </>
                  )}
                  <Form.Item name="field_agents" className="formItem wrapperSect">
                    <CustomSelect 
                      label="Field Agents" 
                      disabled={true} 
                      isMultiSelect={true}
                      options={(agency_profile?.field_agents || []).map(f => ({
                        label: typeof f === "object" ? f.name : `Agent ${f}`,
                        value: typeof f === "object" ? f.id : f
                      }))}
                    />
                  </Form.Item>
                  <Form.Item name="area" className="formItem wrapperSect">
                    <CustomSelect 
                      label="Area" 
                      disabled={true} 
                      isMultiSelect={true}
                      options={(agency_profile?.area || []).map(a => ({
                        label: typeof a === "object" ? String(a.areacode) : `Area ${a}`,
                        value: typeof a === "object" ? a.id : a
                      }))}
                    />
                  </Form.Item>
                </div>
              </PanelCard>
            ) : (
              <>
            <PanelCard
              title="Agency Information"
              icon={<BankOutlined />}
              accent="brand"
              className="profileSection"
            >
              <div className="inputSections">
                <Form.Item
                  name="companyName"
                  className="formItem wrapperSect"
                  rules={[{ required: true, message: "Required" }]}
                >
                  <InputField
                    label="Company Name"
                    placeholder="Enter your Company Name"
                    disabled={true}
                  />
                </Form.Item>

                <Form.Item
                  name="dateTime"
                  className="formItem wrapperSect"
                  rules={[{ required: false, message: "Required" }]}
                >
                  <Flex className="date-time-wrapper">
                    <label className="dateLabel">
                      Registration Date & Time
                    </label>
                    <DatePickerWithTime
                      disabled={true}
                      value={form.getFieldValue("dateTime")}
                      onChange={(date) => form.setFieldValue("dateTime", date)}
                    />
                  </Flex>
                </Form.Item>

                <Form.Item
                  name="presence_in_states"
                  className="formItem wrapperSect"
                  rules={[{ required: true, message: "Select states" }]}
                >
                  <CustomSelect
                    label="Presence in States"
                    placeholder="Select Operational State"
                    isMultiSelect={true}
                    options={[
                      { value: "100001", label: "100001" },
                      { value: "100002", label: "100002" },
                      { value: "100003", label: "100003" },
                    ]}
                  />
                </Form.Item>

                <Form.Item
                  name="cities"
                  className="formItem wrapperSect"
                  rules={[{ required: true, message: "Select cities" }]}
                >
                  <CustomSelect
                    label="Cities"
                    placeholder="Select City"
                    isMultiSelect={true}
                    options={[
                      { value: "Mumbai", label: "Mumbai" },
                      { value: "Delhi", label: "Delhi" },
                      { value: "Chennai", label: "Chennai" },
                    ]}
                  />
                </Form.Item>
              </div>
            </PanelCard>

            <PanelCard
              title="Location Details"
              icon={<EnvironmentOutlined />}
              accent="brand"
              className="profileSection"
            >
              <div className="inputSections">
                <Form.Item
                  name="city"
                  className="formItem wrapperSect"
                  rules={[{ required: true, message: "Enter city" }]}
                >
                  <InputField label="City" placeholder="City" />
                </Form.Item>

                <Form.Item
                  name="pincode"
                  className="formItem wrapperSect"
                  rules={[{ required: true, message: "Enter pin code" }]}
                >
                  <InputField label="Pin Code" placeholder="Pin code" />
                </Form.Item>

                <Form.Item
                  name="registeredAddress"
                  className="formItem wrapperSect"
                  rules={[{ required: true, message: "Enter address" }]}
                >
                  <InputField
                    label="Registered Address"
                    placeholder="Address"
                  />
                </Form.Item>

                <Form.Item
                  name="registeredCity"
                  className="formItem wrapperSect"
                  rules={[
                    { required: true, message: "Select registered city" },
                  ]}
                >
                  <CustomSelect
                    label="Registered City"
                    placeholder="Registered city"
                    options={[]}
                  />
                </Form.Item>

                <Form.Item
                  name="agencyStructure"
                  className="formItem wrapperSect"
                  rules={[{ required: false, message: "Select structure" }]}
                >
                  <CustomSelect
                    label="Agency Structure"
                    placeholder="Agency structure"
                  // options={[]}
                  />
                </Form.Item>

                <Form.Item
                  name="lineOfBusiness"
                  className="formItem wrapperSect"
                  rules={[{ required: true, message: "Select business line" }]}
                >
                  <CustomSelect
                    label="Line of Business"
                    placeholder="Line of business"
                    options={[{ value: "collections", label: "Collections" }]}
                  />
                </Form.Item>

                <Form.Item
                  name="panNo"
                  className="formItem wrapperSect"
                  rules={[{ required: true, message: "Enter PAN" }]}
                >
                  <InputField label="PAN No." placeholder="FEEPE3458A" />
                </Form.Item>

                <Form.Item
                  name="serviceTaxNumber"
                  className="formItem wrapperSect"
                  rules={[{ required: true, message: "Enter tax number" }]}
                >
                  <InputField
                    label="Service Tax Number"
                    placeholder="345893459"
                  />
                </Form.Item>

                <Form.Item
                  name="telephone"
                  className="formItem wrapperSect"
                  rules={[{ required: true, message: "Enter number" }]}
                >
                  <InputField
                    label="Telephone Number"
                    addonBefore="+91"
                    placeholder="9999999999"
                  />
                </Form.Item>
              </div>
            </PanelCard>
            </>
            )}

            {!isStaff && !isSuperuser && (
              <div className="profileActions">
                <NormalButton
                  className="profileSaveBtn"
                  loading={loading}
                  htmlType="submit"
                  width="auto"
                  controlHeight={42}
                >
                  Update Details
                </NormalButton>
              </div>
            )}
          </div>
        </Form>
      </div>
    </div>
  );
};

export default ProfilePage;
